import { z } from 'zod';
import {
  AdminSubdivisionSchema,
  AuthContextResponseSchema,
  BriefingJournalPaperMarkSchema,
  BriefingJournalResponseSchema,
  type BriefingJournalPaperMark,
  type BriefingJournalResponse,
  ORGANIZATION_CONTEXT_HEADER,
  SafetyApprovalAttachmentContentSchema,
  SafetyApprovalAttachmentSchema,
  WorkOrderApprovalRequestSchema,
  WorkOrderForm344Schema,
  type AnnulClearanceDto,
  type AuthContextResponse,
  type SafetyApprovalAttachment,
  type SafetyApprovalAttachmentContent,
  type UpdateClearanceDto,
  type CompleteClearanceBasisDto,
  type WorkOrderApprovalRequest,
  type WorkOrderForm344,
} from '@railroute/contracts/core';
// Корневой ствол контрактов — ЕДИНСТВЕННОЕ место, где панель его читает
// (перепись потребителей: `legacy-compatibility-consumer-audit.stage2-8`).
// Схема выписки наряда и строка работника подразделения живут только там,
// и это те же схемы, которыми их читает телефон. Остальные модули панели
// берут эти типы отсюда, а не из ствола: одна дверь вместо четырёх.
import {
  ClearanceHistoryResponseSchema,
  ClearanceTypeSchema,
  OrganizationWorkTypeSchema,
  PersonnelClearanceSchema,
  SubdivisionMemberSchema,
  WorkerClearancesResponseSchema,
  WorkRequirementMatrixRowSchema,
  WorkSiteSchema,
  type ClearanceCategory,
  type ClearanceHistoryEvent,
  type ClearanceHistoryRecord,
  type ClearanceHistoryResponse,
  type ClearanceType,
  type CreateClearanceDto,
  type CreateWorkOrderDto,
  type ElectricalGroup,
  type InstallationVoltageClass,
  type OrganizationWorkType,
  type PersonnelClearance,
  type SubdivisionMember,
  type WorkerClearancesResponse,
  type WorkRequirementMatrixRow,
  type WorkSite,
} from '@railroute/contracts';
// ADR-008 (issue #508): маршрутный лист — RAIL-подпакет контрактов, а не
// корневой ствол: понятие железнодорожное, и панели незачем ради него тянуть
// совместимостный barrel.
import {
  RouteSheetFormSchema,
  RouteSheetListResponseSchema,
  RouteSheetViewSchema,
  type RouteSheetForm,
  type RouteSheetListResponse,
  type RouteSheetView,
} from '@railroute/contracts/rail';

// Обращения к API.
//
// Почти всё здесь — чтение: панель ведёт разделы по правам и показывает их
// содержимое. Записей две. Первая — выписка наряда (issue #446), и она идёт
// в ТУ ЖЕ ручку `POST /work-orders`, которой наряд выписывают с телефона.
// Второго пути создания нет и не заводится: наряд, выписанный из браузера,
// обязан получиться тем же нарядом, что и выписанный в поле.
//
// Вторая — согласование наряда (issue #445), и совершается оно в два шага,
// потому что согласование есть подписанный документ, а не отметка: сначала
// сервер фиксирует канон и хеш, затем браузер уходит к провайдеру ЭЦП
// подписывать этот хеш. Контур подписи тоже общий с телефоном — второго не
// заводится по тому же доводу.
//
// НИ ОДНОЙ ПРОВЕРКИ ПО СУЩЕСТВУ ЗДЕСЬ НЕТ. Требуемая группа, состав по
// матрице, наблюдающий, напряжение, согласование, полномочия названных лиц —
// считает и отвергает сервер. Панель показывает его отказ словами.
//
// Ответ разбирается схемой из общих контрактов — той же, которой его собирает
// сервер. Разойтись им негде. Там, где сервер отдаёт строку базы без схемы в
// контрактах (`/admin`), схема выписана здесь и охватывает только те поля,
// которые панель показывает.

import { friendlyMessage } from './access-text';

const BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /**
     * Тело отказа целиком. Кроме слов сервер иногда прикладывает разбор —
     * скажем, поимённый перечень неготовых в отказе «Бригада не готова»
     * (`crew`). По нему форма подсвечивает людей в списке.
     */
    readonly body: unknown = null,
  ) {
    super(message);
  }
}

async function request(
  path: string,
  init: RequestInit & { token?: string; organizationId?: string } = {},
): Promise<unknown> {
  const { token, organizationId, ...rest } = init;
  const response = await fetch(`${BASE}${path}`, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      // Организация выбирается явно и уходит заголовком. Без него сервер
      // отказывает всем, у кого членств больше одного: выбрать за клиента он
      // не вправе (TenantContextService, «Multiple active memberships»).
      ...(organizationId ? { [ORGANIZATION_CONTEXT_HEADER]: organizationId } : {}),
      ...(rest.headers ?? {}),
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { message?: unknown };
    const message = typeof body.message === 'string' ? body.message : `Запрос не выполнен (${response.status})`;
    throw new ApiError(response.status, friendlyMessage(response.status, message), body);
  }
  return response.json();
}

export async function login(employeeNumber: string, password: string, role: string): Promise<string> {
  const body = (await request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ employeeNumber, password, role }),
  })) as { accessToken?: unknown };
  if (typeof body.accessToken !== 'string') throw new ApiError(500, 'Сервер не вернул токен');
  return body.accessToken;
}

// Членства вошедшего с правами по каждому. Это и есть источник того, какие
// разделы панель покажет: второй модели прав здесь нет, раскладка повторяет
// то, что сервер уже посчитал по ролям.
export async function fetchAuthContext(token: string): Promise<AuthContextResponse> {
  return AuthContextResponseSchema.parse(await request('/auth/context', { token }));
}

export async function fetchForm344(
  workOrderNumber: string,
  token: string,
  organizationId: string,
): Promise<WorkOrderForm344> {
  const body = await request(`/work-order-forms/${encodeURIComponent(workOrderNumber)}`, {
    token,
    organizationId,
  });
  return WorkOrderForm344Schema.parse(body);
}

/**
 * Issue #445, шаг 1: запросить подпись под согласованием наряда.
 *
 * Идёт по внутреннему id из уже открытого листа, а не по номеру: номер в
 * организации по ошибке бывает задвоен (лист на этот случай отказывается
 * открываться), и второй поиск по номеру завёл бы второе мнение о том, какой
 * это наряд.
 *
 * Наряд после этого вызова ЕЩЁ НЕ СОГЛАСОВАН. Вызов фиксирует канон и хеш
 * документа согласования; согласованным наряд становится, когда под этот хеш
 * придёт квалифицированная подпись (ADR-004 §7).
 */
export async function requestWorkOrderApproval(
  workOrderId: string,
  token: string,
  organizationId: string,
): Promise<WorkOrderApprovalRequest> {
  const body = await request(`/work-order-approvals/${encodeURIComponent(workOrderId)}`, {
    method: 'POST',
    token,
    organizationId,
  });
  return WorkOrderApprovalRequestSchema.parse(body);
}

/**
 * Issue #445, шаг 2: адрес провайдера ЭЦП для подписи этого документа.
 *
 * Та же ручка, которой подписывает документ выдачи мобильное приложение:
 * второго контура подписи в системе нет и заводить его было бы двумя ответами
 * на вопрос «чем подписано». Адрес отдаётся телом, а не редиректом, — панель
 * уходит по нему сама.
 */
export async function signingAuthorizationUrl(
  documentId: string,
  token: string,
  organizationId: string,
): Promise<string> {
  const body = await request(
    `/auth/aitu/start?documentId=${encodeURIComponent(documentId)}`,
    { token, organizationId },
  );
  return z.object({ authorizationUrl: z.string().url() }).parse(body).authorizationUrl;
}

export const OrganizationCardSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  isActive: z.boolean(),
  closureHandoverRequiresManagerCoSignature: z.boolean(),
  // Issue #445: требует ли организация оперативного согласования наряда.
  workOrderRequiresApproval: z.boolean(),
});
export type OrganizationCard = z.infer<typeof OrganizationCardSchema>;

export async function fetchOrganization(token: string, organizationId: string): Promise<OrganizationCard> {
  return OrganizationCardSchema.parse(await request('/admin/organization', { token, organizationId }));
}

export type Subdivision = z.infer<typeof AdminSubdivisionSchema>;

export async function fetchSubdivisions(
  token: string,
  organizationId: string,
  includeInactive = false,
): Promise<Subdivision[]> {
  const body = await request(`/admin/subdivisions?includeInactive=${includeInactive ? 'true' : 'false'}`, {
    token,
    organizationId,
  });
  return z.array(AdminSubdivisionSchema).parse(body);
}

// Основание допуска работника: группа по электробезопасности, медосмотр,
// инструктаж — и срок. Состояние считает СЕРВЕР, тем же расчётом, что и
// готовность бригады; панель его только показывает. Считать здесь заново
// значило бы завести второй ответ на вопрос «проходит ли работник по
// медосмотру» — и он разошёлся бы с первым молча.
//
// Схема общая (issue #446): тот же набор приходит и в списке персонала, и в
// выборе людей при выписке наряда. Выписанная здесь копия разошлась бы с той
// при первой правке одной из них.
export const MembershipClearanceSchema = PersonnelClearanceSchema;
export type MembershipClearance = PersonnelClearance;

export const MembershipRowSchema = z.object({
  id: z.string().uuid(),
  // Табельный номер членства — необязательный: до перехода на нумерацию
  // внутри организации он может стоять только у глобального пользователя.
  employeeNumber: z.string().nullable(),
  subdivisionId: z.string().uuid().nullable(),
  // Название подразделения приходит вместе со строкой работника: право
  // ЧИТАТЬ людей и право МЕНЯТЬ структуру — разные права, и второе здесь ни
  // при чём.
  subdivisionName: z.string().nullable(),
  isActive: z.boolean(),
  user: z.object({
    id: z.string().uuid(),
    name: z.string(),
    isActive: z.boolean(),
    // Issue #531/#533: год рождения — готовым, из ИИН при его вводе. Ручного
    // ввода нет; ИИН панель не видит вовсе.
    birthYear: z.number().int().nullable().optional(),
  }),
  // Issue #531/#533: должность — графа 4 журнала инструктажа вместе с
  // профессией. Вводится правкой сотрудника (PERSONNEL_MANAGE).
  position: z.string().nullable().optional(),
  clearances: z.array(MembershipClearanceSchema),
  overallStatus: z.enum(['admitted', 'warning', 'not_admitted']),
  allowed: z.boolean(),
  // Issue #537: сколько у работника действующих допусков без документа-
  // основания. Считает сервер (`personnel-readiness.ts`); необязательное —
  // сервер без #537 его не присылает, и тогда фильтру показывать нечего.
  clearancesWithoutBasis: z.number().int().optional(),
});
export type MembershipRow = z.infer<typeof MembershipRowSchema>;

export async function fetchMemberships(
  token: string,
  organizationId: string,
  includeInactive = false,
): Promise<MembershipRow[]> {
  const body = await request(`/admin/memberships?includeInactive=${includeInactive ? 'true' : 'false'}`, {
    token,
    organizationId,
  });
  return z.array(MembershipRowSchema).parse(body);
}

/**
 * Должность сотрудника (issue #531/#533). Та же ручка правки сотрудника, что и
 * у администратора организации (`PATCH /admin/memberships/:id`,
 * `PERSONNEL_MANAGE`); пустая строка снимает должность.
 */
export async function updateMembershipPosition(
  token: string,
  organizationId: string,
  membershipId: string,
  position: string,
): Promise<void> {
  await request(`/admin/memberships/${encodeURIComponent(membershipId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ position: position.trim() }),
    token,
    organizationId,
  });
}

/**
 * Журнал регистрации инструктажа за период (issue #531 → #533). Кого видно,
 * решает сервер — по той же области, что персонал: организация целиком или
 * своё подразделение. Панель повторно не фильтрует.
 */
export async function fetchBriefingJournal(
  token: string,
  organizationId: string,
  query: { from: string; to: string; subdivisionId?: string },
): Promise<BriefingJournalResponse> {
  const params = new URLSearchParams({ from: query.from, to: query.to });
  if (query.subdivisionId) params.set('subdivisionId', query.subdivisionId);
  return BriefingJournalResponseSchema.parse(
    await request(`/work-orders/briefing-journal?${params.toString()}`, { token, organizationId }),
  );
}

/** Фото бумажного журнала из строки журнала инструктажа (все страницы). */
export async function fetchBriefingJournalPaperMark(
  token: string,
  organizationId: string,
  paperMarkId: string,
): Promise<BriefingJournalPaperMark> {
  return BriefingJournalPaperMarkSchema.parse(
    await request(`/work-orders/briefing-journal/paper-marks/${encodeURIComponent(paperMarkId)}`, { token, organizationId }),
  );
}

/**
 * Все записи допусков одного работника — для карточки в списке персонала.
 *
 * Та же ручка, которой пользуется мобильное приложение
 * (`GET /clearances/worker/:employeeNumber`): история по каждому виду, номер
 * удостоверения, кем выдано. Второго пути к этим данным не заводится — он
 * разошёлся бы с первым в том, какие записи считаются действующими.
 *
 * ОГРАНИЧЕНИЕ, КОТОРОЕ НАДО ЗНАТЬ. Ручка до сих пор держится и на старой
 * роли входа (SENIOR или ADMIN), а не только на праве CLEARANCE_READ. У
 * инженера по охране труда в демо старая роль SENIOR, и карточка
 * открывается; у работника с правом, но со старой ролью WORKER сервер
 * откажет. Карточка показывает этот отказ словами, а не падает.
 */
export async function fetchWorkerClearances(
  employeeNumber: string,
  token: string,
  organizationId: string,
): Promise<WorkerClearancesResponse> {
  const body = await request(`/clearances/worker/${encodeURIComponent(employeeNumber)}`, {
    token,
    organizationId,
  });
  return WorkerClearancesResponseSchema.parse(body);
}

// ─── Ввод и продление допуска (issue #524) ───────────────────────────────────
//
// Ручка ТА ЖЕ, которой допуск вносят с телефона (`POST /clearances`). Второго
// пути записи не заводится по тому же доводу, что и у наряда: допуск,
// внесённый из браузера, обязан получиться той же записью, что и внесённый в
// поле, и подчиняться тем же правилам ввода.
//
// ПРАВИЛА ВВОДА — СЕРВЕРА (`apps/api/src/clearances/clearance-entry-rules.ts`):
// номер документа, окно дат, группа по электробезопасности. Форма спрашивает
// то же самое до отправки, чтобы человек не ждал отказа ради опечатки, но
// окончательное слово остаётся за сервером, и его отказ показывается словами.

export type { AnnulClearanceDto, ClearanceType, CompleteClearanceBasisDto, CreateClearanceDto, UpdateClearanceDto };

/**
 * Действующие виды допуска ОРГАНИЗАЦИИ — из них выбирают вид при вводе.
 *
 * Не глобальный справочник видов: организация ведёт свой — своё название, своя
 * периодичность, свой порог предупреждения. Взять периодичность из глобального
 * значило бы подставить в форму один срок, а считать допуск по другому.
 */
export async function fetchOrganizationClearanceTypes(
  token: string,
  organizationId: string,
): Promise<ClearanceType[]> {
  const body = await request('/clearance-types/organization', { token, organizationId });
  return z.array(ClearanceTypeSchema).parse(body);
}

/**
 * Внести запись допуска. Продление — это тот же вызов с тем же видом допуска:
 * прежняя запись не меняется и остаётся в истории, а `CLEARANCE_RENEWED` в
 * журнале ставит сервер, увидев у работника прежнюю запись того же вида.
 *
 * Из ответа панель берёт только идентификатор: состояние работника она
 * перечитывает у сервера, а не собирает из тела ответа.
 */
export async function createClearance(
  dto: CreateClearanceDto,
  token: string,
  organizationId: string,
): Promise<{ id: string }> {
  const body = await request('/clearances', {
    method: 'POST',
    body: JSON.stringify(dto),
    token,
    organizationId,
  });
  return z.object({ id: z.string() }).parse(body);
}

// ─── Журнал, исправление и аннулирование допуска (issue #525) ────────────────
//
// Те же три ручки, что завёл сервер в #523. Второго пути к ним нет и не
// заводится: запись, исправленная из браузера, обязана получиться той же
// записью и оставить тот же след в журнале, что исправленная откуда угодно
// ещё.
//
// УДАЛЕНИЯ ЗАПИСИ НЕТ НИГДЕ. Ошибочную запись аннулируют — она перестаёт
// давать допуск и остаётся в истории с ответом на вопросы «кто» и «почему».
// Кнопки «Удалить» в панели нет намеренно, и ручки под неё на сервере больше
// тоже нет.

export type { ClearanceCategory, ClearanceHistoryEvent, ClearanceHistoryRecord, ClearanceHistoryResponse };

/**
 * Журнал допусков работника: все записи, ВКЛЮЧАЯ аннулированные, и события по
 * каждой — ввод, продление, исправление, аннулирование.
 *
 * Отдельная ручка от `fetchWorkerClearances`, и это не дублирование: та
 * отвечает на вопрос «допущен ли он сейчас» и аннулированных записей не
 * показывает вовсе, эта — «как к этому пришли и кто за это отвечает».
 * Считать одно из другого в браузере значило бы завести второй ответ.
 *
 * Право и ограничение те же, что у `fetchWorkerClearances`: ручка до сих пор
 * держится и на старой роли входа (SENIOR или ADMIN), а не только на праве
 * читать допуски.
 */
export async function fetchWorkerClearanceHistory(
  employeeNumber: string,
  token: string,
  organizationId: string,
): Promise<ClearanceHistoryResponse> {
  const body = await request(`/clearances/worker/${encodeURIComponent(employeeNumber)}/history`, {
    token,
    organizationId,
  });
  return ClearanceHistoryResponseSchema.parse(body);
}

/**
 * Исправить запись допуска. Причина обязательна — её требует сервер, и без
 * неё запись не меняется.
 *
 * Вид допуска и работника исправлением не меняют: это не правка документа, а
 * другой документ. Поэтому их нет ни в теле запроса, ни в форме.
 */
export async function updateClearance(
  id: string,
  dto: UpdateClearanceDto,
  token: string,
  organizationId: string,
): Promise<{ id: string }> {
  const body = await request(`/clearances/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(dto),
    token,
    organizationId,
  });
  return z.object({ id: z.string() }).parse(body);
}

/**
 * Дозаполнить основание (issue #537): вписать номер и дату документа в
 * действующую запись, внесённую до того, как они стали обязательными. Окна
 * дат организации здесь нет; правила и отказы — сервера.
 */
export async function completeClearanceBasis(
  id: string,
  dto: CompleteClearanceBasisDto,
  token: string,
  organizationId: string,
): Promise<{ id: string }> {
  const body = await request(`/clearances/${encodeURIComponent(id)}/complete-basis`, {
    method: 'POST',
    body: JSON.stringify(dto),
    token,
    organizationId,
  });
  return z.object({ id: z.string() }).parse(body);
}

/**
 * Аннулировать запись допуска. Причина обязательна; снять аннулирование
 * нельзя — ошибочную запись вводят заново.
 */
export async function annulClearance(
  id: string,
  dto: AnnulClearanceDto,
  token: string,
  organizationId: string,
): Promise<{ id: string }> {
  const body = await request(`/clearances/${encodeURIComponent(id)}/annul`, {
    method: 'POST',
    body: JSON.stringify(dto),
    token,
    organizationId,
  });
  return z.object({ id: z.string() }).parse(body);
}

// ─── Выписка наряда (issue #446) ─────────────────────────────────────────────
//
// Четыре чтения под форму и одна запись. Все ручки — те же, что открывает
// телефон той же роли: справочник видов работ, строка матрицы требований,
// места работ, люди своего подразделения. Ни одной ручки под панель не
// заводится.

export type {
  CreateWorkOrderDto,
  ElectricalGroup,
  InstallationVoltageClass,
  OrganizationWorkType,
  SubdivisionMember,
  WorkerClearancesResponse,
  WorkRequirementMatrixRow,
  WorkSite,
};

/** Справочник видов работ организации. Право — то же `WORK_ORDER_ISSUE`. */
export async function fetchWorkTypes(token: string, organizationId: string): Promise<OrganizationWorkType[]> {
  return z.array(OrganizationWorkTypeSchema).parse(await request('/work-types', { token, organizationId }));
}

/**
 * Строка матрицы требований для вида работы.
 *
 * `null` — строки нет (сервер отвечает 404). Это НЕ ошибка: наряд по такому
 * виду работы выписывается, а требования сервер применит базовые. Показать
 * здесь ошибку значило бы напугать выдающего тем, что работает как задумано.
 */
export async function fetchRequirementMatrixRow(
  workTypeId: string,
  token: string,
  organizationId: string,
): Promise<WorkRequirementMatrixRow | null> {
  try {
    const body = await request(`/work-requirement-matrix/${encodeURIComponent(workTypeId)}`, {
      token,
      organizationId,
    });
    return WorkRequirementMatrixRowSchema.parse(body);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

/** Места работ (станции и перегоны) организации. */
export async function fetchWorkSites(token: string, organizationId: string): Promise<WorkSite[]> {
  return z.array(WorkSiteSchema).parse(await request('/work-sites', { token, organizationId }));
}

/**
 * Люди подразделения выдающего — из них собирается бригада и назначаются
 * ответственные лица. Кого именно видно, решает сервер: круг ограничен
 * подразделением самого вошедшего, и панель его не расширяет.
 */
export async function fetchCrewCandidates(token: string, organizationId: string): Promise<SubdivisionMember[]> {
  return z.array(SubdivisionMemberSchema).parse(await request('/users/subdivision-members', { token, organizationId }));
}

/**
 * Маршрутный лист учёта разъездов за месяц, форма ФРУ № 12 (ADR-008,
 * issue #508).
 *
 * Месяц НЕ ПОДСТАВЛЯЕТСЯ ПАНЕЛЬЮ, когда о нём не спрашивали: текущий месяц
 * считает сервер, по времени Казахстана. Посчитай его браузер — лист зависел бы
 * от часового пояса, выставленного в системе того, кто смотрит.
 *
 * Лист СОБИРАЕТСЯ СЕРВЕРОМ из подтверждённых поездок: строк, номера и причин, по
 * которым лист ещё нельзя сдать, панель не считает — иначе в браузере и на
 * бумаге стояли бы разные листы.
 */
export async function fetchMyRouteSheet(
  token: string,
  organizationId: string,
  month?: string,
): Promise<RouteSheetView> {
  const query = month ? `?month=${encodeURIComponent(month)}` : '';
  return RouteSheetViewSchema.parse(await request(`/rail/route-sheets/my${query}`, { token, organizationId }));
}

/** Лист работника: чей лист виден, решает сервер, а не панель. */
export async function fetchWorkerRouteSheet(
  workerId: string,
  token: string,
  organizationId: string,
  month?: string,
): Promise<RouteSheetView> {
  const query = month ? `?month=${encodeURIComponent(month)}` : '';
  return RouteSheetViewSchema.parse(
    await request(`/rail/route-sheets/worker/${encodeURIComponent(workerId)}${query}`, { token, organizationId }),
  );
}

/**
 * Issue #510: листы за месяц, которые смотрящий вправе видеть. Кого видно,
 * решает сервер — по правам с охватом.
 */
export async function fetchRouteSheetList(
  token: string,
  organizationId: string,
  month?: string,
): Promise<RouteSheetListResponse> {
  const query = month ? `?month=${encodeURIComponent(month)}` : '';
  return RouteSheetListResponseSchema.parse(await request(`/rail/route-sheets${query}`, { token, organizationId }));
}

/**
 * Issue #510: лист в виде бланка ФРУ № 12 — для печати (PDF) и Excel.
 * Утверждённый собирается сервером из подписанных байтов; неутверждённый
 * приходит с `approvedForPayment: false` и печатается с пометкой.
 */
export async function fetchRouteSheetForm(sheetId: string, token: string, organizationId: string): Promise<RouteSheetForm> {
  return RouteSheetFormSchema.parse(
    await request(`/rail/route-sheets/${encodeURIComponent(sheetId)}/form`, { token, organizationId }),
  );
}

// Что панель берёт из ответа на выписку: номер, чтобы назвать выписанный
// наряд, и идентификатор. Остальное она не показывает — наряд открывают
// разделом «Наряд-допуск» или в телефоне.
export const IssuedWorkOrderSchema = z.object({
  id: z.string(),
  number: z.string(),
});
export type IssuedWorkOrder = z.infer<typeof IssuedWorkOrderSchema>;

/**
 * Выписать наряд.
 *
 * Та же ручка и та же схема тела, что у телефона (`CreateWorkOrderSchema` из
 * общих контрактов). Само тело собирается в `work-orders/issue-draft.ts`
 * общими сборками граф, бригады и согласования — теми же, которыми его
 * собирает экран телефона.
 */
export async function createWorkOrder(
  dto: CreateWorkOrderDto,
  token: string,
  organizationId: string,
): Promise<IssuedWorkOrder> {
  const body = await request('/work-orders', {
    method: 'POST',
    body: JSON.stringify(dto),
    token,
    organizationId,
  });
  return IssuedWorkOrderSchema.parse(body);
}

/**
 * Issue #469: приложить к согласованию письменное разрешение или схему.
 *
 * Загрузка ОТДЕЛЬНЫМ шагом, до выписки: наряд выписывается одним запросом, и
 * файл в его теле означал бы, что каждая неудачная выписка гонит десять
 * мегабайт заново. Ответ несёт идентификатор — его и называет тело выписки.
 *
 * Проверок по существу здесь нет, как и везде в этом файле: размер, тип и
 * целостность проверяет сервер и отказывает словами.
 */
export async function uploadSafetyApprovalAttachment(
  file: { fileName: string; mimeType: string; contentBase64: string },
  token: string,
  organizationId: string,
): Promise<SafetyApprovalAttachment> {
  const body = await request('/safety-approval-attachments', {
    method: 'POST',
    body: JSON.stringify(file),
    token,
    organizationId,
  });
  return SafetyApprovalAttachmentSchema.parse(body);
}

/** Issue #469: сами байты приложения — этим лист бланка открывает документ. */
export async function fetchSafetyApprovalAttachment(
  id: string,
  token: string,
  organizationId: string,
): Promise<SafetyApprovalAttachmentContent> {
  const body = await request(`/safety-approval-attachments/${encodeURIComponent(id)}/content`, {
    token,
    organizationId,
  });
  return SafetyApprovalAttachmentContentSchema.parse(body);
}

export function onboardWebRequest<T>(path:string,token:string,organizationId:string,body?:unknown):Promise<T>{return request(`/rail/onboard${path}`,{token,organizationId,...(body===undefined?{}:{method:"POST",body:JSON.stringify(body)})}) as Promise<T>;}
